import { Component } from '@angular/core';
import { DataLayerBaseComponent } from './data-layer-base.component';
import { MapService } from './map.service';
import { DataLayerService } from './data-layer.service';
import { ConfigService } from './config.service';
import { UserMessageService } from './user-message.service';

declare var L;

/*
 * data-layer-geojson-polygon defines a class to display polygon data.  The data format is geojson.
 *
 * Example of the data file:
 * --------------------------------
 * {
"type": "FeatureCollection",
"name": "co_badger_hole_20180420_1712_dd83",
"crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:OGC:1.3:CRS84" } },
"features": [
{ "type": "Feature", "properties": { "OBJECTID": 4, "Shape_Area": 325572985.95,
	"irwinid": "{6F3F1418-AD2F-4C44-8C5E-B6C1430D8948}", "mapmethod": "Unknown",
	"unitIDProt": "COBAX", "unitIDOwn": "COBAX", "incidentID": "2018-COBAX-000574",
	"fireName": "Badger Hole", "perDatTime": "4\/20\/2018 5:12:00 PM",
	"comments": "Event Standard Perims", "agency": "C&L", "active": "N",
	"latest": "Y", "fireYear": "2018", "dateCrnt": "2018\/04\/23", "firecode": "LN9W",
	"fireNum": "000574", "state": "CO", "inComplex": "N", "GISACRES": 50628.70916 },
	"geometry": { "type": "Polygon", "coordinates": [ [ [ -102.00047, 37.36587 ],
	[ -102.00264, 37.36633 ], [ -102.0036, 37.36798 ], [ -102.00434, 37.36965 ], [ -102.00479, 37.37075 ],
 *	  ...
 *	] ] } }] }
 * --------------------------------
 *
 * Within the configuration file, polygon data can be included:
 * "overlayLayers": [
 * 		{"id": "floodplain", "label": "Floodplain", "url": "map/overlay-layers/floodplain.geojson", "popupTitle": "fireName",
 *		 "color": "red", "class": "data-layer-geojson-polygon", "refresh": false },
 * ...
 * ]
 */

@Component({
	selector: 'data-layer-geojson-polygon',
	template: `
		<div class="checkboxDiv">
			<label class="overlay-layer"><input type="checkbox" (click)="menuControlSelected($event)" />{{htmlElementLabel}}</label>
			<app-single-legend [active]=active [legendUrl]=legendUrl></app-single-legend>
		</div>
		`,
	styleUrls: ['app.component.css']
})
export class DataLayerGeojsonPolygonComponent extends DataLayerBaseComponent {

	dataLayerService: DataLayerService;
	configService: ConfigService;
	color = "blue";			// polygon fill color
	fillOpacity = .3;		// polygon fill opacity
	stroke = false;			// polygon outline
	weight = 1;				// polygon outline weight in pixels
	opacity = 1;			// polygon outline opacity

	constructor (
		mapService: MapService, dataLayerService: DataLayerService, configService: ConfigService, userMessageService: UserMessageService ) {

		super (mapService, dataLayerService, configService, userMessageService);

		this.dataLayerService = dataLayerService;
		this.configService = configService;

		// this.htmlElementLabel = "Polygons";
		this.valueObserver = this.valueObservable.asObservable();


	}

	menuControlSelected (event): void {
		if ( event.target.checked ) {
			this.activateLayer();
		} else {
			this.deactivateLayer();
		}

	}

	setData ( someData: any ) {
		// console.log(someData);
		super.setData(someData);

		if ( someData.hasOwnProperty('color')) {
			this.color = someData.color;
		}
		if ( someData.hasOwnProperty('opacity')) {
			this.opacity = someData.opacity;
		}
		if ( someData.hasOwnProperty('fillOpacity')) {
			this.fillOpacity = someData.fillOpacity;
		}
		if ( someData.hasOwnProperty('stroke')) {
			this.stroke = someData.stroke;
		}
		if ( someData.hasOwnProperty('weight')) {
			this.weight = someData.weight;
		}

		// verify the required configuration variable have been specified
		this.verifyLayerConfiguration ( 'data-layer-geojson-polygon', [ 'label', 'url', 'id' ] );
	}

	// refreshLayer first checks to see if the layer is active.  If so, activateLayer is called.
	refreshLayer() {
		if ( this.active === true ) {
			this.activateLayer();
		}
	}

	// first check whether the polygon information has been read.
	// display the polygons on the map and set the layer to active
	activateLayer () {
		console.log('Activating polygon layer.');
		if ( this.mapLayer != null ) {
			this.mapService.map.removeLayer(this.mapLayer);
		}
		if ( this.value == null || this.refresh === true ) {
			this.valueObserver.subscribe ( stations => {
				this.drawPolygons();
			});
			this.parseDataFile();
		} else {
			this.drawPolygons();
		}

		this.active = true;
	}

	drawPolygons () {
		const self = this;
		this.mapLayer = L.geoJson(this.value, {
			// For now hard-code but could determine color from a property
			onEachFeature: this.onEachPolygonFeature,
			style: function (feature) {
				// console.log(feature);
				return { color: self.color, weight: self.weight, opacity: self.opacity, fillOpacity: self.fillOpacity, stroke: self.stroke };
			},
			contents: this
		}, this).addTo(this.mapService.map);
	}

	onEachPolygonFeature(feature, layer) {
		// console.log (this['contents'].popupTitle);

		if ( this['contents'].popupTitle != null ) {
			// Assign HTML content to use in a popup when the user clicks on a polygon
			// Multiple properties may be displayed.  They are specified with a comma-separated string.
			const props = this['contents'].popupTitle.split(',');
			let popupContent = '<span class=\'tooltipHeader\'>' + feature.properties[props[0]]  + '</span><br>';
			for ( let i = 1; i < props.length; i++ ) {
				popupContent += props[i] +  ': ' + feature.properties[props[i]]  + '</br>';
			}
			// console.log(popupContent);

			if (feature.properties && feature.properties.popupContent) {
				// This will append content from a "popupContent" property if it is in the GeoJSON file
				popupContent += feature.properties.popupContent;
			}

			layer.bindPopup(popupContent);
			layer.bindTooltip(popupContent);
		}
	}

	deactivateLayer() {
		console.log('Deactivating polygon layer.');
		if ( this.active === true ) {
			try {
				this.mapService.map.removeLayer(this.mapLayer);
			} catch (err) {
			}
			this.active = false;
		}
	}

	// from data-layer-service.parseFloodPlain
	parseDataFile () {
		console.log('In data-layer-geojson-polygon.parseDataFile.');

		const self = this;
		const requestPolygons = new XMLHttpRequest();
		requestPolygons.onload = function ( ) {
			if ( requestPolygons.readyState === requestPolygons.DONE && requestPolygons.status === 200 ) {

				// first, get json file content
				const polygonBoundaries = JSON.parse(requestPolygons.responseText);
				// console.log(polygonBoundaries);
				console.log('Number of features in ' + self.id + ': ' + polygonBoundaries.features.length);

				self.value = polygonBoundaries;
				self.valueObservable.next(polygonBoundaries);
				console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' +
					filename + ' for all ' + self.id + ' data was successfully completed.');
				// console.log ( dataLayer );

			} else {
				console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' + filename + ' for all ' + self.id + ' data was UNSUCCESSFUL.');
			}
		};
		const filename = this.url.startsWith('http') ? this.url : this.configService.baseDir + this.url + '?nocache=' + (new Date()).getTime();
		requestPolygons.open('get', filename, true );
		requestPolygons.overrideMimeType('text/plain');
		console.log('[' + new Date().toLocaleTimeString() + '] Attempting to read ' + filename + ' for all ' + this.id +  'data.');
		requestPolygons.send();
		}

}
